import { createRoute } from "@tanstack/react-router"

import { OwnerView } from "@/components/dashboard/owner-view"
import { PersonalView } from "@/components/dashboard/personal-view"
import { MorningDeskPage } from "@/components/dashboard/morning-desk"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useGameMode } from "@/contexts/game-mode-context"
import { useUrlSyncedSearchState } from "@/hooks/use-url-synced-search-state"
import { useMorningDesk } from "@/hooks/use-morning-desk"
import { useOwnerData } from "@/hooks/user-owner-data"
import { usePersonalView } from "@/hooks/user-personal-view"
import MainAppRoute from "@/rootRoutes/MainApp"

const DASHBOARD_VIEWS = ["personal", "owner"] as const
type DashboardView = (typeof DASHBOARD_VIEWS)[number]
type DashboardSearch = {
  view?: DashboardView
}

const DashboardRoute = createRoute({
  getParentRoute: () => MainAppRoute,
  path: "/dashboard",
  validateSearch: (search: Record<string, unknown>): DashboardSearch => ({
    view: DASHBOARD_VIEWS.includes(search.view as DashboardView)
      ? (search.view as DashboardView)
      : undefined,
  }),
  component: Dashboard,
})
export default DashboardRoute

/**
 * `/dashboard` is the Morning Desk, on live data.
 *
 * It used to render `canvasDashboardData` — a canvas fixture — for every
 * organization, and `/` redirects here, which made sample figures the product's
 * front door. It now reads `/orgObs/job-risks`, which is what "risk jobs
 * especially is what should eventually feed Needs you" was waiting for.
 *
 * The Personal and Owner views predate the redesign and are not in the canvas,
 * but they carry real features — XP, leaderboard, org metrics — so they stay
 * reachable at `?view=personal` / `?view=owner` rather than being deleted.
 */
function Dashboard() {
  const searchParams = DashboardRoute.useSearch()

  if (!searchParams.view) {
    return <Desk />
  }

  return <LegacyDashboard view={searchParams.view} />
}

function Desk() {
  return <MorningDeskPage desk={useMorningDesk()} />
}

function LegacyDashboard({ view }: { view: DashboardView }) {
  const navigate = DashboardRoute.useNavigate()
  const { patchSearch } = useUrlSyncedSearchState<DashboardSearch>(navigate)
  const { organizationGameMode, gameModeEnabled } = useGameMode()
  const { personalViewData } = usePersonalView()
  const { ownerView: ownerViewData } = useOwnerData()

  const switchTab = (next: string) => {
    if (!DASHBOARD_VIEWS.includes(next as DashboardView)) return
    patchSearch({ view: next as DashboardView })
  }

  return (
    <div className="flex-1 space-y-4 p-4 pt-6 md:p-8">
      <div className="flex items-center justify-between space-y-2">
        <h2 className="text-3xl font-bold tracking-tight">Dashboard</h2>
      </div>

      <Tabs value={view} onValueChange={switchTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="personal">Personal View</TabsTrigger>
          <TabsTrigger value="owner">Owner View</TabsTrigger>
        </TabsList>

        <TabsContent value="personal">
          <PersonalView
            todaysTasks={personalViewData.todaysTasks}
            activeJobs={personalViewData.activeJobs}
            leaderboard={personalViewData.leaderboard}
            stats={personalViewData.stats}
            xpProgress={personalViewData.xpProgress}
            gameModeEnabled={gameModeEnabled}
          />
        </TabsContent>

        <TabsContent value="owner">
          <OwnerView
            orgMetrics={ownerViewData.orgMetrics}
            riskJobs={ownerViewData.riskJobs}
            leaderboard={ownerViewData.leaderboard}
            teamMembersPipelines={ownerViewData.teamMembersPipelines}
            organizationGameMode={organizationGameMode}
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}
